import React, { useContext, useEffect, useMemo, useState } from 'react';
import UserContext from '../../../context/UserContext.js';
import ColumnaBotoneraItem from '../../edicion/ColumnaBotoneraItem.js';
import ResultadoListaPartidos from '../../consulta/ResultadoListaPartidos.js';
import * as DTOs from '../../DTOs/PartidoFixtureDTOs.js';
import * as FuncionesEquipo from '../../funciones/FuncionesEquipo.js';
import * as FuncionesFecha from '../../funciones/FuncionesFecha.js';
import * as FuncionesFixture from '../../funciones/FuncionesFixture.js';
import MensajeCargando from '../../UI/MensajeCargando.js';
import MensajeError from '../../UI/MensajeError.js';
import MensajeGrabando from '../../UI/MensajeGrabando.js';
import ElegirFechaEtapa from './ElegirFechaEtapa.js';
import FilaPartidoFixture from './FilaPartidoFixture.js';
import api from '../../../api/client.js';
import useGetListaURL from '../../../hooks/useGetListaURL.js';
import usePatchURL from '../../../hooks/usePatchURL.js';
import usePostURL from '../../../hooks/usePostURL.js';
import * as Instancia from '../../constantes/InstanciaPartido.js';
import styles from './FixtureEtapaDefinicion.module.css';

const PRIMERA_FECHA = 1;

const normalizarZona = (zona) => {
  if (!zona) {
    return null;
  }

  const valor = zona
    .toString()
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  return valor ? valor.slice(0, 1) : null;
};

const esZonaValida = (zona) => {
  return /^[A-Z0-9]{1}$/.test(zona || '');
};

const coincideConPartidoGrabado = (candidato, existente) => {
  if (!candidato || !existente) {
    return false;
  }

  if (Number(candidato.nroFecha) !== Number(existente.nroFecha)) {
    return false;
  }

  if (candidato.equipoLocal?.id !== existente.equipoLocal?.id) {
    return false;
  }

  const zonaCandidata = normalizarZona(candidato.zona);
  const zonaExistente = normalizarZona(existente.zona);
  if ((zonaCandidata || null) !== (zonaExistente || null)) {
    return false;
  }

  if (candidato.equipoLibre === true) {
    return existente.equipoLibre === true;
  }

  return candidato.equipoVisitante?.id === existente.equipoVisitante?.id;
};

const tieneRivalesValidos = (partido) => {
  return !!partido?.equipoLocal && (partido?.equipoLibre === true || !!partido?.equipoVisitante);
};

const ordenarPreview = (partidos) => {
  return [...(partidos || [])].sort((a, b) => {
    const fechaA = Number(a.nroFecha || 0);
    const fechaB = Number(b.nroFecha || 0);
    if (fechaA !== fechaB) {
      return fechaA - fechaB;
    }
    const zonaA = (a.zona || '').toString();
    const zonaB = (b.zona || '').toString();
    if (zonaA !== zonaB) {
      return zonaA.localeCompare(zonaB);
    }
    return Number(a.orden || a.ordenPartido || 0) - Number(b.orden || b.ordenPartido || 0);
  });
};

const FixtureEtapaGrupos = (props) => {
  const datosElegidos = props.datosElegidos;
  const onPartidoElegido = props.onPartidoElegido; // (idPartido) => void - navega al partido
  const etapa = datosElegidos?.etapa;
  const torneo = datosElegidos?.torneo;
  const { usuario } = useContext(UserContext);
  const puedeEditar = usuario && usuario.puedeEditar;

  const [partidosExistentes, setPartidosExistentes] = useState([]);
  const [partidosFaltantes, setPartidosFaltantes] = useState([]);
  const [partidosFaltantesPorFecha, setPartidosFaltantesPorFecha] = useState({});
  const [equiposDisponibles, setEquiposDisponibles] = useState([]);
  const [partidosEnEdicion, setPartidosEnEdicion] = useState({});
  const [partidoGrabandose, setPartidoGrabandose] = useState(null);
  const [erroresFilaPartido, setErroresFilaPartido] = useState({});
  const [modoFixture, setModoFixture] = useState('carga'); // carga | manual | automatico
  const [fechaSeleccionadaManual, setFechaSeleccionadaManual] = useState(1);
  const [fixtureAutomatico, setFixtureAutomatico] = useState([]);
  const [isGenerandoPreview, setIsGenerandoPreview] = useState(false);
  const [isConfirmandoFixture, setIsConfirmandoFixture] = useState(false);
  const [erroresFixture, setErroresFixture] = useState(null);
  const [isLoadingEtapa, setIsLoadingEtapa] = useState(false);

  const { isCargando, erroresGetLista, fetchData, resetErroresGetLista } = useGetListaURL();
  const { isGrabando, erroresPost, postData, resetErroresPost } = usePostURL();
  const { isGrabando: isGrabandoPatch, erroresPatch, patchData, resetErroresPatch } = usePatchURL();

  const cantidadFechasTotales = useMemo(() => {
    // cantidadFechas ya representa el total de fechas en la etapa de grupos
    return etapa?.cantidadFechas || 0;
  }, [etapa]);

  const partidosFechaUno = useMemo(() => {
    return (partidosExistentes || []).filter(
      (partido) => Number(partido.nroFecha) === PRIMERA_FECHA
    );
  }, [partidosExistentes]);

  const fechaUnoCompleta = useMemo(() => {
    if (!etapa?.cantidadCruces) {
      return false;
    }

    if (partidosFechaUno.length !== etapa.cantidadCruces) {
      return false;
    }

    return partidosFechaUno.every((partido) => {
      if (!tieneRivalesValidos(partido)) {
        return false;
      }
      if (etapa?.zonas === true) {
        return !!normalizarZona(partido.zona);
      }
      return true;
    });
  }, [etapa, partidosFechaUno]);

  const todasLasFechasCompletas = useMemo(() => {
    // Si existen partidos de fechas diferentes a la 1, significa que fueron cargadas
    const tienePartidosFueraFechaUno = (partidosExistentes || []).some(
      (partido) => Number(partido.nroFecha) > PRIMERA_FECHA
    );
    return tienePartidosFueraFechaUno;
  }, [partidosExistentes]);

  const fechaTrabajo =
    modoFixture === 'manual' || todasLasFechasCompletas ? fechaSeleccionadaManual : PRIMERA_FECHA;

  const partidosFechaActual = useMemo(() => {
    return (partidosExistentes || []).filter(
      (partido) => Number(partido.nroFecha) === Number(fechaTrabajo)
    );
  }, [partidosExistentes, fechaTrabajo]);

  useEffect(() => {
    resetErroresGetLista();
    // Al cambiar etapa, solo marcar que estamos cargando
    // NO limpiar datos aún, solo esperar a que lleguen
    if (torneo && etapa && etapa.etapa) {
      setIsLoadingEtapa(true);
      const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
      fetchData(url, (partidos) => {
        // Aquí llegan datos nuevos, recién entonces actualizar TODO
        setPartidosExistentes(partidos || []);
        setPartidosFaltantesPorFecha({});
        setPartidosFaltantes([]);
        setModoFixture('carga');
        setFechaSeleccionadaManual(1);
        setFixtureAutomatico([]);
        setErroresFixture(null);
        setIsLoadingEtapa(false);
      });
      return;
    }

    setPartidosExistentes([]);
    setIsLoadingEtapa(false);
  }, [torneo, etapa, fetchData, resetErroresGetLista]);

  useEffect(() => {
    if (erroresPost?.length > 0 || erroresPatch?.length > 0) {
      setPartidoGrabandose(null);
      if (partidoGrabandose != null) {
        setErroresFilaPartido((prev) => ({
          ...prev,
          [partidoGrabandose]: erroresPost?.length > 0 ? erroresPost : erroresPatch || [],
        }));
      }
    }
  }, [erroresPost, erroresPatch, partidoGrabandose]);

  useEffect(() => {
    if (!torneo) {
      setEquiposDisponibles([]);
      return;
    }

    fetchData(`torneos/${torneo.id}/participantes`, (participantes) => {
      const equipos = (participantes || []).map((p) => p.equipo).filter((e) => e != null);
      setEquiposDisponibles(equipos);
    });
  }, [torneo, fetchData]);

  useEffect(() => {
    if (!etapa || !datosElegidos) {
      setPartidosFaltantes([]);
      setPartidosFaltantesPorFecha({});
      setModoFixture('carga');
      setFechaSeleccionadaManual(1);
      setFixtureAutomatico([]);
      setErroresFixture(null);
      return;
    }

    const fechaActual =
      modoFixture === 'manual' || todasLasFechasCompletas ? fechaSeleccionadaManual : PRIMERA_FECHA;
    const borradorGuardado = partidosFaltantesPorFecha[fechaActual];

    if (borradorGuardado) {
      setPartidosFaltantes(borradorGuardado);
      return;
    }

    const faltantes = FuncionesFixture.PartidosFaltantesGrupos(
      partidosExistentes,
      datosElegidos,
      fechaActual
    );
    const faltantesConOrdinales = asignarOrdinalesToPartidos(faltantes);
    setPartidosFaltantes(faltantesConOrdinales);
  }, [
    partidosExistentes,
    etapa,
    datosElegidos,
    fechaSeleccionadaManual,
    modoFixture,
    partidosFaltantesPorFecha,
    todasLasFechasCompletas,
  ]);

  // useEffect de cleanup ya no es necesario - ahora el reset está en el callback de fetchData

  const recargarPartidos = () => {
    setIsLoadingEtapa(true);
    const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
    fetchData(url, (partidos) => {
      setPartidosExistentes(partidos || []);
      setPartidosFaltantesPorFecha({});
      setPartidosFaltantes([]);
      setModoFixture('carga');
      setFechaSeleccionadaManual(1);
      setFixtureAutomatico([]);
      setErroresFixture(null);
      setIsLoadingEtapa(false);
    });
  };

  const asignarOrdinalesToPartidos = (partidos) => {
    return (partidos || []).map((partido, index) => ({
      ...partido,
      ordenPartido: partido.ordenPartido || index + 1,
      orden: partido.orden || index + 1,
    }));
  };

  const validarPartidoBase = (partido) => {
    if (etapa?.definicion === true && !partido.anioPartido) {
      return 'Debe ingresar la fecha del partido';
    }
    if (!partido.equipoLocal) {
      return 'Debe seleccionar el equipo local';
    }
    if (!partido.equipoLibre && !partido.equipoVisitante) {
      return 'Debe seleccionar el equipo visitante';
    }
    if (!partido.equipoLibre && partido.equipoLocal.id === partido.equipoVisitante.id) {
      return 'Error: El equipo local y visitante no pueden ser el mismo.';
    }

    if (etapa?.zonas === true) {
      const zona = normalizarZona(partido.zona);
      if (!zona || !esZonaValida(zona)) {
        return 'Error: Debe ingresar una zona alfanumérica de 1 carácter.';
      }
    }

    return '';
  };

  const validarEquiposRepetidos = (partidosAValidar, partidosReferencia = []) => {
    const equiposUsados = new Set();

    for (const partido of partidosAValidar) {
      const localId = partido.equipoLocal?.id;
      const visitanteId = partido.equipoVisitante?.id;

      if (!localId) {
        continue;
      }

      if (equiposUsados.has(localId) || (visitanteId && equiposUsados.has(visitanteId))) {
        return 'Error: Un equipo no puede jugar más de un partido en la misma fecha.';
      }

      equiposUsados.add(localId);
      if (visitanteId) {
        equiposUsados.add(visitanteId);
      }
    }

    for (const partido of partidosReferencia) {
      const localId = partido.equipoLocal?.id;
      const visitanteId = partido.equipoVisitante?.id;
      if (!localId) {
        continue;
      }

      if (equiposUsados.has(localId) || (visitanteId && equiposUsados.has(visitanteId))) {
        return 'Error: Uno de los equipos ya está asignado a otro partido de la fecha.';
      }
    }

    return '';
  };

  const normalizarPartidoParaGuardar = (partido) => {
    return {
      ...partido,
      zona: normalizarZona(partido.zona),
      equipoLibre: partido.equipoLibre === true,
      equipoVisitante: partido.equipoLibre === true ? null : partido.equipoVisitante,
      nroFecha: PRIMERA_FECHA,
      codigoInstancia: null,
      observaciones: null,
      dudas: null,
    };
  };

  const onPartidoChange = (index, partidoPatch) => {
    setPartidosFaltantes((prev) => {
      const nuevosPartidos = [...prev];
      const partidoActual = nuevosPartidos[index] || {};
      const actualizado = {
        ...partidoActual,
        ...partidoPatch,
      };
      nuevosPartidos[index] = actualizado;

      // Si se cambió la zona (incluyendo borrar), propagarla a todos los partidos siguientes
      if (partidoPatch.zona !== undefined) {
        const zonaNormalizada = normalizarZona(partidoPatch.zona);
        for (let i = index + 1; i < nuevosPartidos.length; i++) {
          if (nuevosPartidos[i]) {
            nuevosPartidos[i] = {
              ...nuevosPartidos[i],
              zona: zonaNormalizada,
            };
          }
        }
      }

      setPartidosFaltantesPorFecha((prevPorFecha) => ({
        ...prevPorFecha,
        [fechaTrabajo]: nuevosPartidos,
      }));
      return nuevosPartidos;
    });
  };

  const onCancelarPartido = (partido) => {
    const nuevoPartido = DTOs.PartidoFixtureVacio(
      datosElegidos,
      partido.idPartido,
      Instancia.UNICO,
      partido.indiceCruce,
      null,
      PRIMERA_FECHA
    );

    const indexEnFaltantes = partidosFaltantes.findIndex((p) => p.idPartido === partido.idPartido);
    if (indexEnFaltantes === -1) {
      return;
    }

    const nuevosPartidos = [...partidosFaltantes];
    nuevosPartidos[indexEnFaltantes] = nuevoPartido;
    setPartidosFaltantes(nuevosPartidos);
    setPartidosFaltantesPorFecha((prev) => ({
      ...prev,
      [fechaTrabajo]: nuevosPartidos,
    }));
  };

  const onEditarPartido = (idPartido) => {
    const partido = partidosFechaActual.find((p) => p.id === idPartido);
    if (!partido) {
      return;
    }

    const golesLocalOriginal = partido.golesLocal ?? partido.resultadoLocal ?? null;
    const golesVisitanteOriginal = partido.golesVisitante ?? partido.resultadoVisitante ?? null;

    setPartidosEnEdicion((prev) => ({
      ...prev,
      [idPartido]: {
        ...partido,
        zona: normalizarZona(partido.zona),
        golesLocalOriginal,
        golesVisitanteOriginal,
        localEditado: false,
        visitanteEditado: false,
      },
    }));
  };

  const onCancelarEdicion = (idPartido) => {
    setPartidosEnEdicion((prev) => {
      const nuevos = { ...prev };
      delete nuevos[idPartido];
      return nuevos;
    });
  };

  const onEditarTodos = () => {
    const nuevosEnEdicion = { ...partidosEnEdicion };

    for (const partido of partidosFechaActual) {
      if (!nuevosEnEdicion[partido.id]) {
        const golesLocalOriginal = partido.golesLocal ?? partido.resultadoLocal ?? null;
        const golesVisitanteOriginal = partido.golesVisitante ?? partido.resultadoVisitante ?? null;

        nuevosEnEdicion[partido.id] = {
          ...partido,
          zona: normalizarZona(partido.zona),
          golesLocalOriginal,
          golesVisitanteOriginal,
          localEditado: false,
          visitanteEditado: false,
        };
      }
    }

    setPartidosEnEdicion(nuevosEnEdicion);
  };

  const onPartidoEnEdicionChange = (idPartido, partidoPatch) => {
    setPartidosEnEdicion((prev) => {
      const partidoActual = prev[idPartido] || {};
      return {
        ...prev,
        [idPartido]: {
          ...partidoActual,
          ...partidoPatch,
        },
      };
    });
  };

  const onGuardarPartido = (partido) => {
    const errorBase = validarPartidoBase(partido);
    if (errorBase) {
      alert(errorBase);
      return;
    }

    const errorRepetidosNuevos = validarEquiposRepetidos(
      [partido],
      partidosFaltantes.filter(
        (p) => p.idPartido !== partido.idPartido && p.anioPartido && tieneRivalesValidos(p)
      )
    );
    if (errorRepetidosNuevos) {
      alert(errorRepetidosNuevos);
      return;
    }

    const errorRepetidosExistentes = validarEquiposRepetidos([partido], partidosFechaActual);
    if (errorRepetidosExistentes) {
      alert(errorRepetidosExistentes);
      return;
    }

    resetErroresPost();
    setErroresFilaPartido((prev) => {
      const nuevos = { ...prev };
      delete nuevos[partido.idPartido];
      return nuevos;
    });
    setPartidoGrabandose(partido.idPartido);

    const dto = DTOs.SavePartidoFixture(normalizarPartidoParaGuardar(partido));
    postData(`torneos/${torneo.id}/partidos`, [dto], () => {
      setPartidoGrabandose(null);
      setErroresFilaPartido((prev) => {
        const nuevos = { ...prev };
        delete nuevos[partido.idPartido];
        return nuevos;
      });
      setPartidosFaltantes((prev) => prev.filter((p) => p.idPartido !== partido.idPartido));
      setPartidosFaltantesPorFecha((prev) => ({
        ...prev,
        [fechaTrabajo]: prev[fechaTrabajo]?.filter((p) => p.idPartido !== partido.idPartido) || [],
      }));
      recargarPartidos();
    });
  };

  const onGuardarEdicion = (partido) => {
    const errorBase = validarPartidoBase(partido);
    if (errorBase) {
      alert(errorBase);
      return;
    }

    const otrosExistentes = partidosFechaActual.filter((p) => p.id !== partido.id);
    const errorRepetidosExistentes = validarEquiposRepetidos([partido], otrosExistentes);
    if (errorRepetidosExistentes) {
      alert(errorRepetidosExistentes);
      return;
    }

    const errorRepetidosNuevos = validarEquiposRepetidos(
      [partido],
      partidosFaltantes.filter((p) => p.anioPartido && tieneRivalesValidos(p))
    );
    if (errorRepetidosNuevos) {
      alert(errorRepetidosNuevos);
      return;
    }

    resetErroresPatch();
    setErroresFilaPartido((prev) => {
      const nuevos = { ...prev };
      delete nuevos[partido.id];
      return nuevos;
    });
    setPartidoGrabandose(partido.id);

    patchData(
      `partidos/${partido.id}`,
      DTOs.PatchPartidoFixture(normalizarPartidoParaGuardar(partido)),
      () => {
        setPartidoGrabandose(null);
        setErroresFilaPartido((prev) => {
          const nuevos = { ...prev };
          delete nuevos[partido.id];
          return nuevos;
        });
        recargarPartidos();
        onCancelarEdicion(partido.id);
      }
    );
  };

  const onEliminarPartido = async (idPartido) => {
    const confirmar = window.confirm('¿Está seguro que desea eliminar este partido?');
    if (!confirmar) {
      return;
    }

    try {
      const response = await api.delete(`partidos/${idPartido}`);
      if (response.ok) {
        recargarPartidos();
      } else {
        alert(`Error al eliminar partido: ${response.errors?.join(', ') || 'Error desconocido'}`);
      }
    } catch (error) {
      alert(`Error al eliminar partido: ${error.message}`);
    }
  };

  const onGuardarTodos = () => {
    const partidosNuevosValidos = partidosFaltantes
      .map(normalizarPartidoParaGuardar)
      .filter((p) => tieneRivalesValidos(p) && (etapa?.zonas !== true || p.zona));

    const partidosEditadosValidos = Object.values(partidosEnEdicion)
      .map(normalizarPartidoParaGuardar)
      .filter((p) => p && p.id && tieneRivalesValidos(p) && (etapa?.zonas !== true || p.zona));

    const totalParaGuardar = partidosNuevosValidos.length + partidosEditadosValidos.length;
    if (totalParaGuardar === 0) {
      alert('No hay partidos válidos para guardar');
      return;
    }

    const partidosConErrorBase = [...partidosNuevosValidos, ...partidosEditadosValidos].find(
      (partido) => {
        return validarPartidoBase(partido);
      }
    );
    if (partidosConErrorBase) {
      alert(validarPartidoBase(partidosConErrorBase));
      return;
    }

    const idsEditados = new Set(partidosEditadosValidos.map((p) => p.id));
    const referenciaExistentes = partidosFechaActual.filter((p) => !idsEditados.has(p.id));
    const errorRepetidos = validarEquiposRepetidos(
      [...partidosNuevosValidos, ...partidosEditadosValidos],
      referenciaExistentes
    );
    if (errorRepetidos) {
      alert(errorRepetidos);
      return;
    }

    const mensaje = [];
    if (partidosNuevosValidos.length > 0) {
      mensaje.push(`crear ${partidosNuevosValidos.length} partido(s)`);
    }
    if (partidosEditadosValidos.length > 0) {
      mensaje.push(`actualizar ${partidosEditadosValidos.length} partido(s)`);
    }

    const confirmar = window.confirm(`¿Está seguro que desea ${mensaje.join(' y ')}?`);
    if (!confirmar) {
      return;
    }

    if (partidosNuevosValidos.length > 0) {
      resetErroresPost();
      const dtosNuevos = partidosNuevosValidos.map((partido) => DTOs.SavePartidoFixture(partido));
      postData(`torneos/${torneo.id}/partidos`, dtosNuevos, () => {
        const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
        fetchData(url, (partidosActualizados) => {
          const listaActualizada = partidosActualizados || [];
          setPartidosExistentes(listaActualizada);

          const noGuardados = partidosNuevosValidos.filter(
            (partido) =>
              !listaActualizada.some((existente) => coincideConPartidoGrabado(partido, existente))
          );

          setPartidosFaltantes(noGuardados);
          setPartidosFaltantesPorFecha((prev) => ({
            ...prev,
            [fechaTrabajo]: noGuardados,
          }));
          // Resetear al modo carga después de grabar exitosamente
          if (noGuardados.length === 0) {
            setModoFixture('carga');
          }
        });
      });
    }

    if (partidosEditadosValidos.length > 0) {
      resetErroresPatch();
      const dtosEditados = partidosEditadosValidos.map((partido) => ({
        idPartido: partido.id,
        ...DTOs.PatchPartidoFixture(partido),
      }));
      patchData(`torneos/${torneo.id}/partidos`, dtosEditados, () => {
        recargarPartidos();
        setPartidosEnEdicion((prev) => {
          const nuevos = { ...prev };
          partidosEditadosValidos.forEach((p) => {
            delete nuevos[p.id];
          });
          return nuevos;
        });
        // Resetear al modo carga después de grabar exitosamente
        setModoFixture('carga');
      });
    }
  };

  const onCambiarModoFixture = (modoNuevo) => {
    setErroresFixture(null);
    setFixtureAutomatico([]);
    setModoFixture(modoNuevo);
    if (modoNuevo === 'manual') {
      setFechaSeleccionadaManual((valorActual) => valorActual || 1);
    }
  };

  const onFechaManualElegida = (fecha) => {
    setFechaSeleccionadaManual(Number(fecha));
  };

  const onFixtureAutomatico = async () => {
    setIsGenerandoPreview(true);
    setErroresFixture(null);

    try {
      const primeraFecha = ordenarPreview(partidosFechaUno).map((partido) => ({
        idEquipoLocal: partido.equipoLocal?.id || null,
        idEquipoVisitante: partido.equipoVisitante?.id || null,
        zona: normalizarZona(partido.zona),
        orden: partido.ordenPartido || partido.orden || null,
        equipoLibre: partido.equipoLibre || false,
      }));

      const response = await api.post(
        `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/fixture/preview`,
        primeraFecha
      );

      if (!response.ok) {
        setErroresFixture(response.errors || ['No se pudo generar el preview del fixture']);
        return;
      }

      setFixtureAutomatico(response.data || []);
      setModoFixture('automatico');
    } catch (error) {
      setErroresFixture([error.message || 'No se pudo generar el preview del fixture']);
    } finally {
      setIsGenerandoPreview(false);
    }
  };

  const onConfirmarFixtureAutomatico = async () => {
    setIsConfirmandoFixture(true);
    setErroresFixture(null);

    try {
      const body = ordenarPreview(fixtureAutomatico).map((partido) => ({
        codigoCategoria: datosElegidos.categoria?.codigo || null,
        codigoEtapa: etapa.etapa?.codigo || null,
        nroFecha: partido.nroFecha || null,
        ordenPartido: partido.orden || partido.ordenPartido || null,
        idEquipoLocal: partido.idEquipoLocal || null,
        idEquipoVisitante: partido.idEquipoVisitante || null,
        zona: normalizarZona(partido.zona),
        equipoLibre: partido.equipoLibre || false,
      }));

      const response = await api.post(
        `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/fixture`,
        body
      );
      if (!response.ok) {
        setErroresFixture(response.errors || ['No se pudo guardar el fixture automático']);
        return;
      }

      recargarPartidos();
      setModoFixture('carga');
      setFixtureAutomatico([]);
      setPartidosFaltantesPorFecha({});
    } catch (error) {
      setErroresFixture([error.message || 'No se pudo guardar el fixture automático']);
    } finally {
      setIsConfirmandoFixture(false);
    }
  };

  const renderFilaPartidoExistente = (partido) => {
    const enEdicion = !!partidosEnEdicion[partido.id];

    if (enEdicion) {
      return (
        <FilaPartidoFixture
          key={`edit-${partido.id}`}
          partido={partidosEnEdicion[partido.id]}
          equipos={equiposDisponibles}
          torneo={torneo}
          mostrarEquipoLibre={true}
          mostrarZona={etapa?.zonas === true}
          zonaObligatoria={etapa?.zonas === true}
          mostrarOrdenPartido={!todasLasFechasCompletas}
          mostrarFecha={todasLasFechasCompletas}
          mostrarResultados={true}
          esPartidoExistente={true}
          requiereFecha={false}
          esVuelta={false}
          isGrabando={partidoGrabandose === partido.id}
          onChange={(partidoActualizado) =>
            onPartidoEnEdicionChange(partido.id, partidoActualizado)
          }
          onGuardar={() => onGuardarEdicion(partidosEnEdicion[partido.id])}
          onCancelar={() => onCancelarEdicion(partido.id)}
          onPartidoElegido={onPartidoElegido}
          errores={erroresFilaPartido[partido.id] || []}
        />
      );
    }

    let resultado = '';
    if (partido.equipoLibre === false) {
      resultado =
        partido.resultadoLocal != null && partido.resultadoVisitante != null
          ? `${partido.resultadoLocal} - ${partido.resultadoVisitante}`
          : '-';
    }

    return (
      <tr key={partido.id}>
        {!todasLasFechasCompletas && (
          <td className={styles.ordenPartido}>{partido.ordenPartido || ''}</td>
        )}
        {todasLasFechasCompletas && (
          <td className={styles.fecha}>
            {FuncionesFecha.CamposToFechaCorta(
              partido.anioPartido,
              partido.mesPartido,
              partido.diaPartido
            )}
          </td>
        )}
        <td className={styles.equipoLocal}>
          {FuncionesEquipo.NombreEquipoTorneo(partido.equipoLocal, torneo)}
        </td>
        <td className={styles.resultado}>
          {partido.equipoLibre === true ? (
            <span>{resultado}</span>
          ) : onPartidoElegido ? (
            <ResultadoListaPartidos
              id={partido.id}
              local={partido.resultadoLocal}
              visitante={partido.resultadoVisitante}
              onPartidoElegido={onPartidoElegido}
            />
          ) : (
            <span>{resultado}</span>
          )}
        </td>
        <td className={styles.equipoVisitante}>
          {partido.equipoLibre ? (
            <i>Libre</i>
          ) : (
            FuncionesEquipo.NombreEquipoTorneo(partido.equipoVisitante, torneo)
          )}
        </td>
        {etapa?.zonas === true && (
          <td className={styles.zona}>{normalizarZona(partido.zona) || ''}</td>
        )}
        <ColumnaBotoneraItem
          idItem={partido.id}
          onEditarItem={onEditarPartido}
          onEliminarItem={onEliminarPartido}
          className={styles.botones}
        />
      </tr>
    );
  };

  const renderPreviewFixture = () => {
    if (!fixtureAutomatico || fixtureAutomatico.length === 0) {
      return null;
    }

    const gruposPorFecha = ordenarPreview(fixtureAutomatico).reduce((acumulado, partido) => {
      const fecha = Number(partido.nroFecha || 0);
      if (!acumulado[fecha]) {
        acumulado[fecha] = [];
      }
      acumulado[fecha].push(partido);
      return acumulado;
    }, {});

    const fechasOrdenadas = Object.keys(gruposPorFecha)
      .map((valor) => Number(valor))
      .sort((a, b) => a - b);

    return (
      <div className={styles.previewFixture}>
        {fechasOrdenadas.map((fecha) => (
          <div key={`preview-fecha-${fecha}`} className={styles.previewFecha}>
            <div className={styles.previewTituloFecha}>Fecha {fecha}</div>
            <table className={styles.previewTabla}>
              <thead>
                <tr>
                  <th className={styles.previewColOrden}>#</th>
                  {etapa?.zonas === true && <th className={styles.previewColZona}>Zona</th>}
                  <th className={styles.previewColEquipo}>Local</th>
                  <th className={styles.previewColEquipo}>Visitante</th>
                </tr>
              </thead>
              <tbody>
                {gruposPorFecha[fecha].map((partido, index) => (
                  <tr key={`${fecha}-${index}`}>
                    <td className={styles.previewColOrden}>
                      {partido.orden || partido.ordenPartido || ''}
                    </td>
                    {etapa?.zonas === true && (
                      <td className={styles.previewColZona}>
                        {normalizarZona(partido.zona) || ''}
                      </td>
                    )}
                    <td className={styles.previewColEquipo}>
                      {partido.nombreEquipoLocal ||
                        partido.nombreLocal ||
                        partido.equipoLocal?.nombre ||
                        `Equipo ${partido.idEquipoLocal || ''}`}
                    </td>
                    <td className={styles.previewColEquipo}>
                      {partido.equipoLibre
                        ? '(libre)'
                        : partido.nombreEquipoVisitante ||
                          partido.nombreVisitante ||
                          partido.equipoVisitante?.nombre ||
                          `Equipo ${partido.idEquipoVisitante || ''}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  if (!etapa || etapa.definicion === true) {
    return null;
  }

  // Mostrar solo "Cargando" mientras se cargan datos de una nueva etapa
  if (isLoadingEtapa) {
    return (
      <div className={styles.contenedor}>
        <MensajeCargando isCargando={true} />
      </div>
    );
  }

  return (
    <div className={styles.contenedor}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <h3 style={{ margin: 0 }}>Fixture de {etapa.etapa?.descripcion || 'Etapa'}</h3>
        {puedeEditar &&
          (modoFixture === 'carga' || todasLasFechasCompletas) &&
          partidosFechaActual.length > 0 && (
            <button
              onClick={onEditarTodos}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#0078d4',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              Editar todos
            </button>
          )}
      </div>

      {modoFixture === 'carga' && fechaUnoCompleta && !todasLasFechasCompletas && (
        <div className={styles.accionesModo}>
          <button
            className={styles.btnModoFixture}
            onClick={onFixtureAutomatico}
            disabled={isGenerandoPreview}
          >
            Fixture automático
          </button>
          <button
            className={styles.btnModoFixtureSecundario}
            onClick={() => onCambiarModoFixture('manual')}
            disabled={isGenerandoPreview}
          >
            Fixture manual
          </button>
        </div>
      )}

      {modoFixture === 'automatico' && (
        <div className={styles.grupo}>
          <h4>Preview del fixture completo</h4>
          {renderPreviewFixture()}
          <div className={styles.accionesModo}>
            <button
              className={styles.btnModoFixture}
              onClick={onConfirmarFixtureAutomatico}
              disabled={isConfirmandoFixture}
            >
              Confirmar fixture
            </button>
            <button
              className={styles.btnModoFixtureSecundario}
              onClick={() => onCambiarModoFixture('carga')}
              disabled={isConfirmandoFixture}
            >
              Volver
            </button>
          </div>
        </div>
      )}

      {(modoFixture === 'manual' || todasLasFechasCompletas) && cantidadFechasTotales > 0 && (
        <div className={styles.grupo}>
          <ElegirFechaEtapa
            etapa={{ ...etapa, cantidadFechas: cantidadFechasTotales }}
            fechaElegida={fechaSeleccionadaManual}
            onFechaElegida={onFechaManualElegida}
          />
          {modoFixture === 'manual' && (
            <div className={styles.accionesModo}>
              <button
                className={styles.btnModoFixtureSecundario}
                onClick={() => onCambiarModoFixture('carga')}
                disabled={isGenerandoPreview}
              >
                Volver
              </button>
            </div>
          )}
        </div>
      )}

      {modoFixture !== 'automatico' && (
        <div className={styles.grupo}>
          <h4>
            {todasLasFechasCompletas
              ? `Fecha ${fechaTrabajo}`
              : modoFixture === 'manual'
                ? `Fecha ${fechaTrabajo}`
                : 'Primera fecha'}
          </h4>
          <table className={styles.tabla}>
            <colgroup>
              {!todasLasFechasCompletas && <col className={styles.ordenPartido} />}
              {todasLasFechasCompletas && <col className={styles.fecha} />}
              <col className={styles.equipoLocal} />
              <col className={styles.resultado} />
              <col className={styles.equipoVisitante} />
              {etapa?.zonas === true && <col className={styles.zona} />}
              <col className={styles.botones} />
            </colgroup>
            <thead>
              <tr>
                {!todasLasFechasCompletas && <th className={styles.ordenPartido}>#</th>}
                {todasLasFechasCompletas && <th className={styles.fecha}>Día</th>}
                <th className={styles.equipoLocal}>Local</th>
                <th className={styles.resultado}>Res</th>
                <th className={styles.equipoVisitante}>Visitante</th>
                {etapa?.zonas === true && <th className={styles.zona}>Zona</th>}
                <th className={styles.botones}></th>
              </tr>
            </thead>
            <tbody>
              {partidosFechaActual.map((partido) => renderFilaPartidoExistente(partido))}

              {puedeEditar &&
                partidosFaltantes.map((partido) => {
                  const indexEnFaltantes = partidosFaltantes.findIndex(
                    (p) => p.idPartido === partido.idPartido
                  );
                  return (
                    <FilaPartidoFixture
                      key={`faltante-${partido.idPartido}`}
                      partido={partido}
                      torneo={torneo}
                      equipos={equiposDisponibles}
                      mostrarEquipoLibre={true}
                      mostrarZona={etapa?.zonas === true}
                      zonaObligatoria={etapa?.zonas === true}
                      mostrarOrdenPartido={!todasLasFechasCompletas}
                      mostrarFecha={todasLasFechasCompletas}
                      mostrarResultados={false}
                      requiereFecha={false}
                      esVuelta={false}
                      isGrabando={partidoGrabandose === partido.idPartido}
                      onChange={(partidoActualizado) =>
                        onPartidoChange(indexEnFaltantes, partidoActualizado)
                      }
                      onGuardar={() => onGuardarPartido(partido)}
                      onCancelar={() => onCancelarPartido(partido)}
                      onPartidoElegido={onPartidoElegido}
                      errores={erroresFilaPartido[partido.idPartido] || []}
                    />
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {modoFixture !== 'automatico' &&
        puedeEditar &&
        (partidosFaltantes.length > 0 || Object.keys(partidosEnEdicion).length > 0) && (
          <div className={styles.acciones}>
            <button
              onClick={onGuardarTodos}
              disabled={isGrabando || isGrabandoPatch}
              className={styles.btnGuardarTodos}
            >
              Guardar todos los partidos editados
            </button>
            <MensajeGrabando
              isGrabando={isGrabando || isGrabandoPatch}
              entidad="partido"
              plural={true}
              estilo={styles.mensajeGrabandoTodos}
            />
          </div>
        )}

      <MensajeCargando isCargando={isCargando || isGenerandoPreview} />
      <MensajeGrabando
        isGrabando={isConfirmandoFixture}
        entidad="fixture"
        estilo={styles.mensajeGrabandoTodos}
      />
      <MensajeError errores={erroresGetLista} />
      <MensajeError errores={erroresPost} />
      <MensajeError errores={erroresPatch} />
      <MensajeError errores={erroresFixture} />
    </div>
  );
};

export default FixtureEtapaGrupos;
